/**
 * Agent avatars — a stored recipe, never an image.
 *
 * `{ shape, seed, palette?, energy? }` renders the same "Orb" SVG everywhere
 * (web, API, PNG for channels). An agent without a stored recipe still gets a
 * stable one derived from its id. The SVG exposes named parts (`.gaze`, `.lid`,
 * `.eyes-*`, `.m-*`, `.drift`) so the UI can animate blinking, gaze and mouth.
 *
 * Everything here is deterministic: no Math.random, no runtime-dependent sort.
 */

export const AGENT_AVATAR_SHAPES = ['orb', 'blob', 'pill'] as const;
export type AgentAvatarShape = (typeof AGENT_AVATAR_SHAPES)[number];

export const AGENT_AVATAR_ENERGIES = ['calm', 'normal', 'lively'] as const;
export type AgentAvatarEnergy = (typeof AGENT_AVATAR_ENERGIES)[number];

/** Curated so every random seed looks good; keys are stored, so never rename one. */
export const AGENT_AVATAR_PALETTES = {
  aurora: ['#5b5bd6', '#8b5cf6', '#22d3ee', '#a7f3d0', '#f0abfc'],
  atardecer: ['#f97316', '#fb7185', '#fde68a', '#7c3aed', '#fff1e6'],
  bosque: ['#0f766e', '#34d399', '#bef264', '#fef9c3', '#155e75'],
  oceano: ['#0ea5e9', '#1e3a8a', '#67e8f9', '#e0f2fe', '#6366f1'],
  durazno: ['#fb923c', '#fecaca', '#fde68a', '#f472b6', '#fff7ed'],
  lavanda: ['#c4b5fd', '#f5d0fe', '#a5b4fc', '#fbcfe8', '#4c1d95'],
  cereza: ['#be123c', '#fb7185', '#fecdd3', '#4c0519', '#ffe4e6'],
  grafito: ['#1f2937', '#6b7280', '#e5e7eb', '#a78bfa', '#f9fafb'],
} as const satisfies Record<string, readonly string[]>;
export type AgentAvatarPalette = keyof typeof AGENT_AVATAR_PALETTES;
const PALETTE_KEYS = Object.keys(AGENT_AVATAR_PALETTES) as AgentAvatarPalette[];

export interface AgentAvatar {
  shape: AgentAvatarShape;
  seed: string;
  /** Omitted = picked from the seed. */
  palette?: AgentAvatarPalette;
  /** How restless the idle motion is. Omitted = normal. */
  energy?: AgentAvatarEnergy;
}

/** Motion speed multiplier per energy level. */
export const AGENT_AVATAR_ENERGY_SPEED: Record<AgentAvatarEnergy, number> = { calm: 0.7, normal: 1, lively: 1.5 };

const SEED_MAX = 64;

/** FNV-1a 32-bit. */
export function hashSeed(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** mulberry32 stream seeded from a string. */
function seededRandom(seed: string): () => number {
  let a = hashSeed(seed);
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function defaultAgentAvatar(agentId: string): AgentAvatar {
  return { shape: 'orb', seed: agentId };
}

/** What the "shuffle" button produces: same look settings, new seed. */
export function shuffleAgentAvatar(current: AgentAvatar, random: () => number = Math.random): AgentAvatar {
  return { ...current, seed: random().toString(36).slice(2, 10).padEnd(8, '0') };
}

export function validateAgentAvatar(
  raw: unknown,
): { ok: true; value: AgentAvatar } | { ok: false; error: string } {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return { ok: false, error: '`avatar` must be an object { shape, seed, palette?, energy? } or null' };
  }
  const { shape, seed, palette, energy } = raw as Record<string, unknown>;
  if (!AGENT_AVATAR_SHAPES.includes(shape as AgentAvatarShape)) {
    return { ok: false, error: `\`avatar.shape\` must be one of: ${AGENT_AVATAR_SHAPES.join(', ')}` };
  }
  if (typeof seed !== 'string' || !seed.trim() || seed.length > SEED_MAX) {
    return { ok: false, error: `\`avatar.seed\` must be a non-empty string up to ${SEED_MAX} chars` };
  }
  if (palette !== undefined && !PALETTE_KEYS.includes(palette as AgentAvatarPalette)) {
    return { ok: false, error: `\`avatar.palette\` must be one of: ${PALETTE_KEYS.join(', ')}` };
  }
  if (energy !== undefined && !AGENT_AVATAR_ENERGIES.includes(energy as AgentAvatarEnergy)) {
    return { ok: false, error: `\`avatar.energy\` must be one of: ${AGENT_AVATAR_ENERGIES.join(', ')}` };
  }
  return {
    ok: true,
    value: {
      shape: shape as AgentAvatarShape,
      seed: seed.trim(),
      ...(palette ? { palette: palette as AgentAvatarPalette } : {}),
      ...(energy ? { energy: energy as AgentAvatarEnergy } : {}),
    },
  };
}

/** Stored value (possibly null or stale) → a renderable recipe. Never throws. */
export function resolveAgentAvatar(stored: unknown, agentId: string): AgentAvatar {
  const parsed = stored == null ? null : validateAgentAvatar(stored);
  return parsed?.ok ? parsed.value : defaultAgentAvatar(agentId);
}

export function agentAvatarPalette(avatar: AgentAvatar): readonly string[] {
  return AGENT_AVATAR_PALETTES[avatar.palette ?? PALETTE_KEYS[hashSeed(avatar.seed) % PALETTE_KEYS.length]];
}

/** Per-avatar rhythm so a list of agents never moves in lockstep (seconds). */
export function agentAvatarRhythm(seed: string) {
  const h = hashSeed(seed);
  const pick = (shift: number, min: number, span: number) => min + (((h >>> shift) % 1000) / 1000) * span;
  return { float: pick(0, 3.2, 1.8), phase: -pick(5, 0, 4) };
}

function luminance(hex: string): number {
  const n = parseInt(hex.slice(1), 16);
  return (0.299 * (n >> 16) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255;
}

function blobPath(radius: number, seed: string): string {
  const rand = seededRandom(seed);
  const harmonics = [2, 3, 4, 5].map((k) => ({ k, a: ((0.4 + rand()) * 6.4) / k, p: rand() * Math.PI * 2 }));
  let d = '';
  for (let i = 0; i < 72; i++) {
    const t = (i / 72) * Math.PI * 2;
    const r = radius + harmonics.reduce((sum, h) => sum + h.a * Math.sin(h.k * t + h.p), 0);
    d += `${i ? 'L' : 'M'}${(50 + r * Math.cos(t)).toFixed(2)},${(50 + r * Math.sin(t)).toFixed(2)}`;
  }
  return `${d}Z`;
}

const f = (n: number) => Number(n.toFixed(2));

export interface RenderAgentAvatarOptions {
  /** Prefix for SVG ids; must be unique per rendered instance on a page. */
  idPrefix?: string;
  /** Include the face (eyes, mouth). Default true. */
  face?: boolean;
  /** Include SMIL shape morphing for `blob`. Default true. */
  animate?: boolean;
}

/**
 * Render the Orb as an SVG string (viewBox 0 0 100 100). Only numbers and
 * palette constants are interpolated, never the seed, so the output is safe
 * to inject as markup.
 */
export function renderAgentAvatarSvg(avatar: AgentAvatar, options: RenderAgentAvatarOptions = {}): string {
  const id = (options.idPrefix ?? `av${hashSeed(avatar.seed).toString(36)}`).replace(/[^a-zA-Z0-9_-]/g, '');
  const rand = seededRandom(avatar.seed);
  const mix = [...agentAvatarPalette(avatar)];
  for (let i = mix.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [mix[i], mix[j]] = [mix[j], mix[i]];
  }
  const [base, ...rest] = mix;

  const blob = blobPath(44, avatar.seed);
  const shape = avatar.shape === 'blob'
    ? `<path d="${blob}">${options.animate === false ? '' : `<animate attributeName="d" dur="${f(7 + rand() * 4)}s" repeatCount="indefinite" calcMode="spline" keySplines=".45 0 .55 1;.45 0 .55 1" values="${blob};${blobPath(44, `${avatar.seed}~`)};${blob}"/>`}</path>`
    : avatar.shape === 'pill'
      ? '<rect x="7" y="7" width="86" height="86" rx="30"/>'
      : '<circle cx="50" cy="50" r="44"/>';

  const blobs = rest.slice(0, 3).map((color) => {
    const cx = 20 + rand() * 60, cy = 20 + rand() * 60, r = 26 + rand() * 18;
    const dx = (rand() - 0.5) * 38, dy = (rand() - 0.5) * 38, dur = 7 + rand() * 6;
    return `<g class="drift" style="--dx:${f(dx)}px;--dy:${f(dy)}px;--d:${f(dur)}s"><circle cx="${f(cx)}" cy="${f(cy)}" r="${f(r)}" fill="${color}"/></g>`;
  }).join('');

  const avg = mix.slice(0, 4).reduce((sum, c) => sum + luminance(c), 0) / 4;
  const ink = avg > 0.52 ? '#1b1b2b' : '#ffffff';
  const glint = ink === '#ffffff' ? '#1b1b2b' : '#ffffff';
  const gap = 8.5 + rand() * 4.5, ey = 46 + rand() * 4, ew = 4.8 + rand() * 1.6, eh = ew * (1.25 + rand() * 0.5);
  const L = 50 - gap, R = 50 + gap, my = ey + eh / 2 + 7, smile = 3.2 + rand() * 1.6;
  const eye = (x: number) =>
    `<rect x="${f(x - ew / 2)}" y="${f(ey - eh / 2)}" width="${f(ew)}" height="${f(eh)}" rx="${f(ew / 2)}" fill="${ink}"/>` +
    `<circle cx="${f(x + ew * 0.18)}" cy="${f(ey - eh * 0.22)}" r="${f(ew * 0.2)}" fill="${glint}" opacity=".9"/>`;
  const arc = (x: number, up: boolean) =>
    `<path d="M${f(x - ew * 0.8)} ${f(ey + (up ? 1.5 : 0))}q${f(ew * 0.8)} ${f(up ? -eh * 0.75 : eh * 0.5)} ${f(ew * 1.6)} 0"/>`;
  const brow = (x: number) => `<path d="M${f(x - ew * 0.7)} ${f(ey - eh * 0.95)}q${f(ew * 0.7)} -2.5 ${f(ew * 1.4)} 0"/>`;

  const face = options.face === false ? '' : `<g class="gaze">` +
    `<g class="brows-up" stroke="${ink}" stroke-width="1.6" stroke-linecap="round" fill="none">${brow(L)}${brow(R)}</g>` +
    `<g class="brows-worried" stroke="${ink}" stroke-width="1.6" stroke-linecap="round">` +
      `<path d="M${f(L - ew * 0.8)} ${f(ey - eh * 0.7)}L${f(L + ew * 0.6)} ${f(ey - eh * 1.05)}"/>` +
      `<path d="M${f(R + ew * 0.8)} ${f(ey - eh * 0.7)}L${f(R - ew * 0.6)} ${f(ey - eh * 1.05)}"/></g>` +
    `<g class="eyes-open"><g class="lid">${eye(L)}${eye(R)}</g></g>` +
    `<g class="eyes-happy" stroke="${ink}" stroke-width="2" stroke-linecap="round" fill="none">${arc(L, true)}${arc(R, true)}</g>` +
    `<g class="eyes-closed" stroke="${ink}" stroke-width="1.8" stroke-linecap="round" fill="none">${arc(L, false)}${arc(R, false)}</g>` +
    `<path class="m-smile" d="M45 ${f(my - 1)}q5 ${f(smile)} 10 0" stroke="${ink}" stroke-width="1.8" stroke-linecap="round" fill="none"/>` +
    `<path class="m-flat" d="M46.5 ${f(my)}h7" stroke="${ink}" stroke-width="1.8" stroke-linecap="round"/>` +
    `<circle class="m-o" cx="50" cy="${f(my)}" r="2.4" fill="${ink}"/>` +
    `<g class="m-talkwrap"><ellipse class="m-talk" cx="50" cy="${f(my)}" rx="3.8" ry="3" fill="${ink}"/></g>` +
    `<path class="sweat" d="M${f(R + ew + 4)} ${f(ey - eh - 3)}q2.2 3.5 0 5q-2.2 -1.5 0 -5z" fill="#7dd3fc"/>` +
    `</g>`;

  return `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">` +
    `<defs>` +
      `<clipPath id="${id}c">${shape}</clipPath>` +
      `<filter id="${id}b" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="11"/></filter>` +
      `<filter id="${id}n"><feTurbulence type="fractalNoise" baseFrequency=".9" numOctaves="2" stitchTiles="stitch"/><feColorMatrix values="0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 .5 0"/></filter>` +
      `<radialGradient id="${id}h" cx=".32" cy=".26" r=".6"><stop offset="0" stop-color="#fff" stop-opacity=".55"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></radialGradient>` +
      `<radialGradient id="${id}s" cx=".5" cy=".5" r=".5"><stop offset=".7" stop-color="#000" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity=".2"/></radialGradient>` +
    `</defs>` +
    `<g clip-path="url(#${id}c)">` +
      `<rect width="100" height="100" fill="${base}"/>` +
      `<g filter="url(#${id}b)">${blobs}</g>` +
      `<rect width="100" height="100" filter="url(#${id}n)" opacity=".16" style="mix-blend-mode:overlay"/>` +
      `<rect width="100" height="100" fill="url(#${id}h)"/><rect width="100" height="100" fill="url(#${id}s)"/>` +
    `</g>${face}</svg>`;
}
